import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { ServiceAction, ProductAction, ServiceModalAction, AlertAction } from '../../actions';
import { history } from '../../history';
import ServiceInfoView from '../../components/service/ServiceInfoView';
import ServiceInfoStatus from '../../components/service/ServiceInfoStatus';
import ServiceComments from '../../components/service/ServiceInfoComments';
import ServiceInfoLogs from '../../components/service/ServiceInfoLogs';

import ServiceInfoAttachments from '../../components/service/ServiceInfoAttachments';
import ServiceClaimCategory from '../../components/service/ServiceClaimCategory';
import SiteInfo from '../../components/site/SiteInfo';
import ProductErrorInfo from '../../components/product/ProductErrorInfo';
import ServiceProductErrorRegister from '../../components/service/ServiceProductErrorRegister';
import ProductInfo from '../../components/product/ProductInfo';
import ServiceReplacement from '../../components/service/ServiceReplacement';
import { ServiceStatusIcon } from '../../components/service/ServiceCard';
import { ccrGeneratePDF } from '../../components/common/ExportPdfReport';

import CustomFieldValues from '../../components/customfield/CustomFieldValues';
import { NoRecord } from '../HomePage';
import { Translate as t } from '../../config/Translate';
import { Loading } from '../../components/common/Loading';

const AdminServiceInfo = ({ GetServiceByTicketKey, service, GetProduct, product, user, UpdateService, GetPendingAlerts, NotifySuccess }) => {

	//const [t, i18n] = useTranslation();
	const { ticketKey, view } = useParams();
	const [isLoading, setIsLoading] = useState(true);
	const [tab, setTab] = useState(view || "comment");
	const [editable, setEditable] = useState(false);
	const [delAlert, setDelAlert] = useState(false);

	// nodata 는 state 가 아니라 렌더 시 계산한다 (ServiceDetail 과 동일 패턴).
	// state + useEffect([service]) 로 세팅하면 언마운트 없이 route param 만 A→B 로 바뀌고
	// B 가 not-found 라 service 참조가 갱신되지 않는 경우 effect 가 재실행되지 않아
	// 게이트가 열린 채 stale A 편집 UI 가 렌더된다.
	const nodata = !ticketKey || String(ticketKey) !== String(service?.ticketKey ?? '');

	useEffect(() => {
		if (service.ticketKey) {
			setIsLoading(false);
		}
		if (service.sn) {
			GetProduct(service.sn);
		}
	}, [service])

	useEffect(() => {
		// BUG-156: not-found(빈) 응답은 SERVICE_GET_ONE 을 dispatch 하지 않아 위 [service] effect 가
		// 재실행되지 않는다. GetServiceByTicketKey(ticketKey) 의 반환값(redux-thunk 는 fetch 사이클
		// 완료 시 resolve 되는 Promise 를 반환)을 fetch 완료 신호로 사용해 로딩을 확실히 해제한다.
		let cancelled = false;
		setIsLoading(true);
		Promise.resolve(GetServiceByTicketKey(ticketKey)).finally(() => {
			if (!cancelled) setIsLoading(false);
		});
		return () => { cancelled = true; };
	}, [ticketKey])

	const getLengthStr = (objArr) => {
		if (objArr) {
			return objArr.length > 0 ? "(" + objArr.length + ")" : "";
		}
		return "";
	}

	const tabList = [
		{ code: "comment", title: t('common.label.comments') + getLengthStr(service.comments) },
		{ code: "Replacements", title: t('service.label.replacements') + getLengthStr(service.replacements) },
		{ code: "logs", title: t('common.label.logs') + getLengthStr(service.histories) }
	]

	const DelService = async () => {
		const result = await ServiceAction.DeleteSimple(service._id);
		if (result) {
			// 삭제 성공 안내 — 페이지 이동·재조회(await) 전에 띄운다
			NotifySuccess?.(t('common.message.deleted'));
			// BUG-060: 삭제 성공 후 경고 뱃지 갱신 (관련 ServiceModalAlert는 BE에서 정리됨)
			// fire-and-forget: ServiceModalWarningIcon은 글로벌(Admin Layout) 컴포넌트이므로
			// 페이지 이동 후에도 dispatch 결과가 정상 반영됨
			GetPendingAlerts();
			history.push("/Admin/AdminServiceList");
		}
	}

	const ExportCCR = async () => {
		const result = await ServiceAction.GetForExport(service.ticketKey);
		if (result.data.status == "success") {
			ccrGeneratePDF(result.data.data, t);
		} else {
			//ERROR!
		}
	}

	const UpdateExtra = (code, value) => {
		UpdateService(service._id, {
			customfieldvals: { ...service.customfieldvals, [code]: value }
		});
	}

	const ServiceReport = ()=>{
		history.push({
			pathname: '/Admin/AdminServiceExportCSR',
			state: {
				item: service,
			},
		});
	}

	return (
		<>
			{isLoading && (
				<div id="appCapsule" className="extra-header-active full-height">
					<div className="d-flex justify-content-center align-items-center" style={{ height: '60vh' }}>
						<Loading />
					</div>
				</div>
			)}
			{!isLoading &&
				<>
					{(!nodata && service._id) && <>
						<div className="extraHeader">
							<h2 className="mb-0">
								<div className="float-start">
									<ServiceStatusIcon item={service} css="item" />
								</div>
								<div className="float-start mt-05 ms-1">
									{service.summary}&nbsp;
								</div>
							</h2>
							<div className="ray-right">
								{!user?.isMegagen && <button className="btn btn-success btn-sm mt-0 me-1" onClick={() => ServiceReport()}>{t('service.label.serviceReport')}</button>}
								<button className="btn btn-danger btn-sm mt-0 me-1" onClick={() => setDelAlert(true)}>{t('service.button.deleteTicket')}</button>
								<Link to="/Admin/AdminServiceList" className="btn btn-outline-default btn-sm mt-0 me-1">{t('common.label.list')}</Link>
							</div>
						</div>
						<div id="appCapsule" className="extra-header-active full-height">
							<div className="section mt-1 mb-1">
								{!delAlert &&
									<>
										<div className="row">
											<div className="col pe-1">
												<div className="card mb-1">
													<h3 className="card-header">{t('common.label.information')}</h3>
													<ServiceInfoView service={service} editable={editable} onEdit={() => setEditable(true)} onCancel={() => setEditable(false)} callBackUpdate={() => setEditable(false)} />
												</div>
												<div className="card mb-1">
													<div className="card m-1">
														<ul className="nav nav-tabs lined">
															{tabList.map(x => (
																<li className="nav-item" key={x.code}>
																	<a className={tab == x.code ? "nav-link active" : "nav-link"}
																		onClick={() => setTab(x.code)}>{x.title}</a>
																</li>
															))}
														</ul>
														<div className="pt-0">
															{/* tab == "Attachments" && <ServiceInfoAttachments service={service} />*/}
															{tab == "Replacements" && <ServiceReplacement service={service} onlyContents={true} />}
															{tab == "comment" && <ServiceComments service={service} />}
															{tab == "logs" && <ServiceInfoLogs service={service} />}
														</div>
													</div>
												</div>
											</div>
											<div className="col ps-0">
												<div className="row">
													<div className="col">
														<div className="card mb-1">
															<ServiceClaimCategory editable={editable} key={service} />
														</div>
														{service.attachments &&
															<div className="card mb-1">
																<h3 className="card-header">{t('common.label.attachments')}</h3>
																<div className='card'>
																	<ServiceInfoAttachments service={service} />
																</div>
															</div>}
														<div className="card mb-1">
															<h3 className="card-header">{t('user.title')}/{t('common.field.date')}</h3>
															<ServiceInfoStatus service={service} editable={editable} nocard={true} />
														</div>
													</div>
													<div className="col ps-0">
														<CustomFieldValues model={"SERVICE"} update={UpdateExtra} data={service.customfieldvals || {}} />
														{service.productErrorCodeId &&
															<div className="card mb-1">
																<h3 className="card-header">{t('product.label.errorInformation')}</h3>
																<ProductErrorInfo productErrorCodeId={service.productErrorCodeId} sn={service.sn} nocard={true} />
															</div>}
														{!service.productErrorCodeId && service.sn &&
															<div className="card mb-1">
																<h3 className="card-header">{t('product.label.errorInformation')}</h3>
																<ServiceProductErrorRegister serviceId={service._id} sn={service.sn} />
															</div>}
														{product &&
															<div className="card mb-1">
																<h3 className="card-header">{t('product.label.productInfo')}</h3>
																<ProductInfo productInfo={product} showsn={true} nocard={true} />
															</div>}
														{service.siteId &&
															<div className="card mb-1">
																<h3 className="card-header">{t('site.label.siteInfo')}</h3>
																<SiteInfo siteId={service.siteId} showTitle={true} nocard={true} />
															</div>}
													</div>
												</div>

											</div>
										</div>
									</>
								}
								{delAlert &&
									<>
										<div className="card bg-danger text-white">
											<div className="card-header">
												<div className="card-title">경고!</div>
											</div>
											<div className="card-body">
												<div className="card-title">즉시 반영됩니다!!!</div>
												<p>이 작업은 되돌릴 수 없는 작업입니다. 정말 삭제하시겠습니까?</p>
											</div>
										</div>
										<button className="btn btn-danger" onClick={() => DelService()}>{t('common.button.delete')}</button>
										<button className="btn btn-default ml-2" onClick={() => setDelAlert(false)}>{t('common.button.cancel')}</button>
									</>
								}
							</div>
						</div>
					</>}
					{/* BUG-156 review Major-2: service._id 요구 시 최초 진입 not-found(SERVICE_GET_ONE 미dispatch로
						service._id 도 undefined) 상태가 두 분기 모두에서 걸러져 빈 화면이 된다. NoRecord 블록은
						static t() 라벨/Link 뿐이라 빈 service 에도 안전하므로 service._id 조건을 제거한다. */}
					{nodata && <><div className="extraHeader">
						<h2 className="mb-0">
							<div className="float-start">{t('service.label.serviceInformation')}</div>
							<div className="float-start mt-05 ms-1">
							</div>
						</h2>
						<div className="ray-right">
							<Link to="/Admin/AdminServiceList" className="btn btn-outline-default btn-sm mt-0 me-1">{t('common.label.list')}</Link>
						</div>
					</div>
						<div id="appCapsule" className="extra-header-active full-height">
							<div className="pt-1">
								<NoRecord />
							</div>
						</div>
					</>}
				</>
			}
			{isLoading && <div className="" style={{ marginTop : 120 }}>
				<Loading></Loading>
			</div>}
		</>
	);
};

const mapState = (state) => {
	const service = state.ServiceReducer.service;
	const product = state.ProductReducer.product;
	const user = state.AuthReducer.user;

	return { service, product, user };
};

const mapDispatch = (dispatch) => ({
	GetServiceByTicketKey: (ticketKey) => dispatch(ServiceAction.GetServiceByTicketKey(ticketKey)),
	GetProduct: (sn) => dispatch(ProductAction.GetProduct(sn)),
	UpdateService: (_id, data) => dispatch(ServiceAction.UpdateService(_id, data)),
	GetPendingAlerts: () => dispatch(ServiceModalAction.GetPendingAlerts()),
	NotifySuccess: (message) => dispatch(AlertAction.Success(message)),
});

export default connect(mapState, mapDispatch)(AdminServiceInfo);